import { useEffect, useState } from 'react';
import PageLayout from '../../components/feature/PageLayout';
import PageHero from '../../components/feature/PageHero';

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    organization: '',
    email: '',
    phone: '',
    message: '',
    preferredContact: '',
    location: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [showPopup, setShowPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [phoneError, setPhoneError] = useState('');

  const validatePhone = (value: string): boolean => {
    const digits = value.replace(/\D/g, '');
    if (digits.length !== 10) {
      setPhoneError('Phone number must be exactly 10 digits.');
      return false;
    }
    setPhoneError('');
    return true;
  };

  const handlePhoneChange = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 10);
    let formatted = digits;
    if (digits.length >= 7) {
      formatted = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    } else if (digits.length >= 4) {
      formatted = `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    } else if (digits.length > 0) {
      formatted = `(${digits}`;
    }
    setFormData((prev) => ({ ...prev, phone: formatted }));
    if (phoneError) validatePhone(formatted);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validatePhone(formData.phone)) return;

    setIsSubmitting(true);
    setSubmitStatus('idle');
    setShowPopup(false);
    setErrorMessage('');

    try {
      const response = await fetch(`${SUPABASE_URL}/functions/v1/send-contact-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setShowPopup(true);
        setFormData({
          name: '',
          organization: '',
          email: '',
          phone: '',
          message: '',
          preferredContact: '',
          location: '',
        });
      } else {
        const data = await response.json().catch(() => ({}));
        setErrorMessage(data?.error || 'Something went wrong. Please try again.');
        setSubmitStatus('error');
        setShowPopup(true);
      }
    } catch {
      setErrorMessage('Unable to send message. Please check your connection and try again.');
      setSubmitStatus('error');
      setShowPopup(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);

    // SEO Meta Tags
    document.title = 'Contact Us | Evernorth AI | Northwest Georgia AI Consulting';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Get in touch with Evernorth AI for AI consulting, training workshops, and readiness audits in Northwest Georgia. Call (706) 331-5858 or schedule a discovery call today.');
    }

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL}/contact`);
    }

    // Schema.org JSON-LD
    const schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "name": "Contact Evernorth AI",
      "description": "Contact Evernorth AI for AI consulting and training services in Northwest Georgia",
      "url": `${import.meta.env.VITE_SITE_URL}/contact`,
      "mainEntity": {
        "@type": "LocalBusiness",
        "name": "Evernorth AI",
        "telephone": "+1-706-331-5858",
        "email": "info@evernorthai.com",
        "areaServed": {
          "@type": "State",
          "name": "Georgia"
        },
        "address": {
          "@type": "PostalAddress",
          "addressRegion": "GA",
          "addressCountry": "US"
        }
      }
    });
    document.head.appendChild(schemaScript);

    return () => {
      document.head.removeChild(schemaScript);
    };
  }, []);

  return (
    <PageLayout>
      <PageHero
        badge="Get In Touch"
        title="Ready to Start Your"
        titleHighlight="AI Journey?"
        description="Let's discuss how we can help your business embrace AI and future-proof your operations"
      />

      <section className="py-24 bg-[#f8f8f9]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <form onSubmit={handleSubmit} data-readdy-form id="contact-page-form" className="space-y-6">
                <p className="text-charcoal text-sm mb-2">Please share a few details so we can understand how to help:</p>

                <div>
                  <label htmlFor="cp-name" className="block text-sm font-semibold text-navy mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="cp-name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 bg-white border border-soft-gray/50 rounded-lg text-navy text-sm focus:outline-none focus:border-gold transition-colors"
                    placeholder="John Smith"
                  />
                </div>

                <div>
                  <label htmlFor="cp-organization" className="block text-sm font-semibold text-navy mb-2">
                    Organization
                  </label>
                  <input
                    type="text"
                    id="cp-organization"
                    name="organization"
                    value={formData.organization}
                    onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                    className="w-full px-4 py-3 bg-white border border-soft-gray/50 rounded-lg text-navy text-sm focus:outline-none focus:border-gold transition-colors"
                    placeholder="Your Organization"
                  />
                </div>

                <div>
                  <label htmlFor="cp-email" className="block text-sm font-semibold text-navy mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="cp-email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 bg-white border border-soft-gray/50 rounded-lg text-navy text-sm focus:outline-none focus:border-gold transition-colors"
                    placeholder="john@company.com"
                  />
                </div>

                <div>
                  <label htmlFor="cp-phone" className="block text-sm font-semibold text-navy mb-2">
                    Phone *
                  </label>
                  <input
                    type="tel"
                    id="cp-phone"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={(e) => handlePhoneChange(e.target.value)}
                    onBlur={() => { if (formData.phone) validatePhone(formData.phone); }}
                    className={`w-full px-4 py-3 bg-white border rounded-lg text-navy text-sm focus:outline-none transition-colors ${phoneError ? 'border-red-400 focus:border-red-400' : 'border-soft-gray/50 focus:border-gold'}`}
                    placeholder="(555) 123-4567"
                  />
                  {phoneError && (
                    <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1">
                      <i className="ri-error-warning-line w-3 h-3 flex items-center justify-center text-xs"></i>
                      {phoneError}
                    </p>
                  )}
                  <p className="text-soft-gray text-xs mt-1">Must be 10 digits (e.g., 5551234567 or (555) 123-4567)</p>
                </div>

                <div>
                  <label htmlFor="cp-message" className="block text-sm font-semibold text-navy mb-2">
                    How can we help you? *
                  </label>
                  <textarea
                    id="cp-message"
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="w-full px-4 py-3 bg-white border border-soft-gray/50 rounded-lg text-navy text-sm focus:outline-none focus:border-gold transition-colors resize-none"
                    placeholder="Tell us about your business and how we can help..."
                  ></textarea>
                  <div className="text-xs text-soft-gray mt-1">
                    {formData.message.length}/500 characters
                  </div>
                </div>

                <div>
                  <label htmlFor="cp-preferred-contact" className="block text-sm font-semibold text-navy mb-2">
                    Preferred Contact Method *
                  </label>
                  <select
                    id="cp-preferred-contact"
                    name="preferredContact"
                    required
                    value={formData.preferredContact}
                    onChange={(e) => setFormData({ ...formData, preferredContact: e.target.value })}
                    className="w-full px-4 py-3 bg-white border border-soft-gray/50 rounded-lg text-navy text-sm focus:outline-none focus:border-gold transition-colors cursor-pointer appearance-none"
                    style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 12 12\'%3E%3Cpath fill=\'%23666\' d=\'M6 8L1 3h10z\'/%3E%3C/svg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 16px center' }}
                  >
                    <option value="" disabled>Select a contact method</option>
                    <option value="Email">Email</option>
                    <option value="Phone call">Phone call</option>
                    <option value="Text Message (SMS)">Text Message (SMS)</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="cp-location" className="block text-sm font-semibold text-navy mb-2">
                    Where are you located?
                  </label>
                  <input
                    type="text"
                    id="cp-location"
                    name="location"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    className="w-full px-4 py-3 bg-white border border-soft-gray/50 rounded-lg text-navy text-sm focus:outline-none focus:border-gold transition-colors"
                    placeholder="City, State"
                  />
                </div>

                <div className="relative">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full px-8 py-4 bg-gold text-white font-semibold rounded-lg hover:bg-[#a68a4e] transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </button>

                  {showPopup && submitStatus === 'success' && (
                    <div className="absolute left-0 right-0 -bottom-16 animate-fade-in-up">
                      <div className="flex items-center gap-2 px-4 py-3 bg-green-50 border border-green-200 rounded-lg shadow-lg">
                        <i className="ri-checkbox-circle-fill text-green-500 text-lg w-5 h-5 flex items-center justify-center"></i>
                        <p className="text-green-700 text-sm font-medium">Thank you! We&apos;ll get back to you soon.</p>
                        <button
                          type="button"
                          onClick={() => { setShowPopup(false); setSubmitStatus('idle'); }}
                          className="ml-auto text-green-400 hover:text-green-600 cursor-pointer"
                        >
                          <i className="ri-close-line text-base w-4 h-4 flex items-center justify-center"></i>
                        </button>
                      </div>
                    </div>
                  )}

                  {showPopup && submitStatus === 'error' && (
                    <div className="absolute left-0 right-0 -bottom-20 animate-fade-in-up z-10">
                      <div className="flex items-start gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-lg shadow-lg">
                        <i className="ri-error-warning-fill text-red-500 text-lg w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                        <div className="flex-1">
                          <p className="text-red-700 text-sm font-medium">
                            {errorMessage || 'Something went wrong. Please try again.'}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => { setShowPopup(false); setSubmitStatus('idle'); setErrorMessage(''); }}
                          className="text-red-400 hover:text-red-600 cursor-pointer flex-shrink-0"
                        >
                          <i className="ri-close-line text-base w-4 h-4 flex items-center justify-center"></i>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </form>
            </div>

            {/* Contact Info & Calendly */}
            <div className="space-y-8">
              {/* Contact Information Card */}
              <div className="bg-white p-8 rounded-2xl border border-soft-gray/30 shadow-sm">
                <h3 className="text-2xl font-bold mb-6 text-navy">Contact Information</h3>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-navy/10 rounded-lg flex-shrink-0">
                      <i className="ri-map-pin-line text-2xl text-navy"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-navy mb-1">Service Area</h4>
                      <p className="text-charcoal text-sm">Northwest Georgia Region</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-navy/10 rounded-lg flex-shrink-0">
                      <i className="ri-mail-line text-2xl text-navy"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-navy mb-1">Email</h4>
                      <p className="text-charcoal text-sm">info@evernorthai.com</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-navy/10 rounded-lg flex-shrink-0">
                      <i className="ri-phone-line text-2xl text-navy"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-navy mb-1">Phone</h4>
                      <p className="text-charcoal text-sm">706-331-5858</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-navy/10 rounded-lg flex-shrink-0">
                      <i className="ri-time-line text-2xl text-navy"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-navy mb-1">Business Hours</h4>
                      <p className="text-charcoal text-sm">Monday - Friday: 9:00 AM - 5:00 PM (EST)</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Calendly Inline Widget */}
              <div className="bg-gradient-to-br from-navy to-[#062299] p-8 rounded-2xl shadow-2xl">
                <i className="ri-calendar-check-line text-5xl text-gold mb-4"></i>
                <h3 className="text-2xl font-bold mb-3 text-white">Free Consultation</h3>
                <p className="text-white/90 mb-6">
                  Schedule a free 15-minute consultation to discuss your AI needs and how we can help your business thrive.
                </p>
                <a
                  href="https://calendly.com/evernorthai/discovery"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block px-8 py-3 bg-gold text-white font-semibold rounded-full hover:bg-[#a68a4e] transition-all cursor-pointer whitespace-nowrap"
                >
                  Book Now
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}
